import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GalleryHorizontalEnd, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useGetChatMediaInfiniteQuery } from "@/services/(my-space)/api.chat";
import { cn } from "@/utils/cn";
import { FileCard } from "./ChatCard";

type Tab = "media" | "documents";
/** Tab ids double as the API's `kind` filter — data, not labels. */
const TABS: Tab[] = ["media", "documents"];

/**
 * Chat Info → Media gallery. Media: photos and videos sent as media, in a
 * grid (photos open the viewer, videos a player). Files: everything sent with
 * "File", as download rows — the same split the chat itself shows.
 */
export const MediaGallery = ({
  conversationId,
  onOpenImage,
}: {
  conversationId: number;
  onOpenImage: (urls: string[], index: number) => void;
}) => {
  const { t } = useTranslation("chat");
  const [tab, setTab] = useState<Tab>("media");
  const [video, setVideo] = useState<string | null>(null);
  const query = useGetChatMediaInfiniteQuery({ conversationId, kind: tab });
  const items = query.data?.pages.flatMap((p) => p.data) ?? [];
  const photos = items.filter((a) => a.kind === "image");

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 px-1">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          <GalleryHorizontalEnd className="size-3.5" />
          {t("info.media_gallery")}
        </h3>
        <div className="inline-flex rounded-md bg-muted p-0.5" role="tablist">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                "rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
                tab === value
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {value === "media" ? t("info.tab_media") : t("info.tab_files")}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        tab === "media" ? (
          <div className="grid grid-cols-3 gap-1">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="aspect-square rounded-xs" />
            ))}
          </div>
        ) : (
          <Skeleton className="h-12 w-full rounded-sm" />
        )
      ) : items.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {tab === "media" ? t("info.no_media") : t("info.no_files")}
        </p>
      ) : tab === "media" ? (
        <div className="grid grid-cols-3 gap-1">
          {items.map((a) =>
            a.kind === "video" ? (
              <button
                key={`${a.messageId}-${a.fileId}`}
                type="button"
                onClick={() => setVideo(a.fileUrl)}
                aria-label={t("video.play")}
                className="relative aspect-square overflow-hidden rounded-xs bg-black"
              >
                <video
                  src={`${a.fileUrl}#t=0.1`}
                  muted
                  playsInline
                  preload="metadata"
                  className="pointer-events-none size-full object-cover"
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex size-8 items-center justify-center rounded-full bg-black/55 text-white">
                    <Play className="size-3.5 translate-x-px fill-current" />
                  </span>
                </span>
              </button>
            ) : (
              <button
                key={`${a.messageId}-${a.fileId}`}
                type="button"
                onClick={() => onOpenImage(photos.map((x) => x.fileUrl), photos.indexOf(a))}
                className="aspect-square overflow-hidden rounded-xs bg-muted"
              >
                <img src={a.fileUrl} alt="" loading="lazy" className="size-full object-cover" />
              </button>
            ),
          )}
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {items.map((a) => (
            <li key={`${a.messageId}-${a.fileId}`}>
              <FileCard attachment={a} isMine={false} className="w-full" />
            </li>
          ))}
        </ul>
      )}

      {query.hasNextPage ? (
        <Button
          variant="ghost"
          size="sm"
          isLoading={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {t("info.show_more")}
        </Button>
      ) : null}

      <Dialog open={!!video} onOpenChange={(v) => !v && setVideo(null)}>
        <DialogContent className="w-auto max-w-[95vw] border-0 bg-black p-0 sm:max-w-[90vw]">
          <DialogTitle className="sr-only">{t("video.play")}</DialogTitle>
          {video ? (
            <video src={video} controls autoPlay playsInline className="max-h-[85vh] max-w-full" />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
};
